import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { SidebarChromeContext } from "../components/OneRowChrome.jsx";
import { DevActAsControl } from "../components/DevActAsControl.jsx";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Activity, Home, FileText, Radio,
  FolderKanban, Compass,
  BarChart3, Settings, ChevronsLeft, Bell, Search,
  ChevronDown, Map, ListChecks,
  Target, BookOpen, LogOut, Camera,
} from "lucide-react";
import { LAYERS, T } from "../app/tokens.js";
import { useHoverIntent } from "../components/useHoverIntent.js";
import { UniversalSearchOpenContext } from "../contexts/UniversalSearchContext.jsx";
import { APP_NAME } from "../constants/branding.js";
import { getRailNavItems } from "../components/NavRail.jsx";
import { useFeatureFlagContext } from "../contexts/FeatureFlagContext.jsx";
import { getInitials } from "../utils/initials.js";
import { useAccountPhoto } from "../components/useAccountPhoto.js";
import { Popover } from "../components/Popover.jsx";
import { ProfilePhotoDialog, ChangePhotoAvatar } from "../components/profilePhoto/ProfilePhotoDialog.jsx";
import { resolveAccountPhotoUrl } from "../components/profilePhoto/cropMath.js";
import { getRoleDisplayLabel, useSensitivityPolicy } from "../contexts/SensitivityPolicyContext.jsx";
import { useDataReconciliationSummary } from "../hooks/useDataReconciliationSummary.js";
import { HomeHeader } from "./home/HomeHeader.jsx";
import "./index.css";
import "../components/objectChrome.css";
import "../components/oneRowChrome.css";

// LOCATION-HEADER-COLLAPSE-S1: Extracted from ShellV2 topbar so the identical
// control cluster (search · bell · user chip + dropdown) can be
// placed in both the global white top bar AND the LocationDetail unified bar.
// Each mount owns its own state; only one is ever visible at a time.
export function UniversalSearchTrigger({
  onOpenSearch,
  onCloseSearch,
  onClearSearch,
  onQueryChange,
  onKeyDown,
  inputRef,
  open = false,
  query = "",
  modal = false,
}) {
  if (modal) {
    return (
      <div data-testid="universal-search-trigger" className="v2-universal-search-trigger one-row-search-field is-open">
        <Search className="v2-universal-search-icon one-row-search-field-icon" size={20} aria-hidden="true" />
        <input
          ref={inputRef}
          data-testid="universal-search-input"
          className="v2-universal-search-input one-row-search-field-input"
          value={query}
          onChange={(event) => { onOpenSearch?.(); onQueryChange?.(event.target.value); }}
          onFocus={onOpenSearch}
          onKeyDown={onKeyDown}
          placeholder="Search offices, leases, projects, markets… or ask a question"
          aria-label="Search portfolio"
          aria-keyshortcuts="Meta+K Control+K"
          aria-expanded={open}
          autoComplete="off"
          spellCheck={false}
        />
        <button
          type="button"
          data-testid="universal-search-close"
          className="one-row-search-esc"
          aria-label="Close search"
          title="Close search (Esc)"
          onMouseDown={(event) => event.preventDefault()}
          onClick={onCloseSearch}
        >Esc</button>
      </div>
    );
  }
  return (
    <div
      data-testid="universal-search-trigger"
      className={`v2-universal-search-trigger${open ? " is-open" : ""}`}
      style={{
        display: "flex", alignItems: "center", gap: 8,
        background: "var(--v2-blue-tint)",
        border: "1px solid var(--v2-blue-border)",
        borderRadius: open ? "9px 9px 0 0" : 9, padding: "7px 10px",
        color: "var(--v2-mid)", fontSize: T.fsSm5, fontWeight: T.fwRegular,
        cursor: "text",
      }}
    >
      <Search className="v2-universal-search-icon" size={15} aria-hidden="true" />
      <span data-testid="universal-search-label" className="v2-universal-search-label" aria-hidden="true" style={{ display: "none" }}>Search portfolio…</span>
      <input
        ref={inputRef}
        data-testid="universal-search-input"
        className="v2-universal-search-input"
        value={query}
        onChange={(event) => {
          onOpenSearch?.();
          onQueryChange?.(event.target.value);
        }}
        onFocus={onOpenSearch}
        onKeyDown={onKeyDown}
        placeholder="Search portfolio…"
        aria-label="Search portfolio"
        aria-keyshortcuts="Meta+K Control+K"
        aria-expanded={open}
        style={{
          flex: 1, minWidth: 0, width: "100%",
          border: 0, padding: 0, color: "var(--v2-dark)",
          background: "transparent", fontSize: T.fsMd,
        }}
      />
      {open || query ? (
        <button
          type="button"
          data-testid="universal-search-clear"
          aria-label={query ? "Clear search" : "Close search"}
          onMouseDown={(event) => event.preventDefault()}
          onClick={query ? onClearSearch : onCloseSearch}
          style={{ border: 0, background: "transparent", color: "var(--v2-mid)", cursor: "pointer", display: "grid", placeItems: "center", padding: 0 }}
        >
          <span aria-hidden="true" style={{ fontSize: T.fsMd, lineHeight: 1 }}>×</span>
        </button>
      ) : (
        <span data-testid="universal-search-key" className="v2-universal-search-key" aria-hidden="true">⌘K</span>
      )}
    </div>
  );
}

// Shared by collection and record headers; the single input and palette are
// portalled together so opening search never changes header geometry.
export function CenteredSearchAnchor({
  onOpenSearch, onCloseSearch, onClearSearch, onQueryChange, onSearchKeyDown,
  searchInputRef, searchOpen, searchQuery, searchPalette,
}) {
  const triggerRef = useRef(null);
  const dialogRef = useRef(null);
  useLayoutEffect(() => {
    if (!searchOpen) return undefined;
    const trigger = triggerRef.current;
    searchInputRef?.current?.focus();
    const trapFocus = (event) => {
      if (event.key !== "Tab") return;
      const focusable = [...(dialogRef.current?.querySelectorAll('input, button:not(:disabled), a[href], [tabindex="0"]') ?? [])]
        .filter((element) => element.getClientRects().length > 0);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !dialogRef.current?.contains(document.activeElement))) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && !dialogRef.current?.contains(document.activeElement)) {
        event.preventDefault(); first?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first?.focus();
      }
    };
    document.addEventListener("keydown", trapFocus);
    return () => {
      document.removeEventListener("keydown", trapFocus);
      trigger?.focus();
    };
  }, [searchOpen, searchInputRef]);
  return (
    <div data-testid="v2-search-anchor" className="one-row-search">
      <button type="button" ref={triggerRef} className="one-row-search-button"
        data-testid="universal-search-icon-trigger" aria-label="Search portfolio"
        aria-haspopup="dialog" aria-expanded={searchOpen} aria-keyshortcuts="Meta+K Control+K"
        title="Search portfolio (⌘K / Ctrl+K)" onClick={onOpenSearch}><Search size={18} /></button>
      {searchOpen && createPortal(
        <div className="one-row-search-overlay" data-testid="universal-search-modal-overlay"
          style={{ "--one-row-search-scrim": T.modalOverlay, "--one-row-search-shadow": T.shadowModal, zIndex: LAYERS.modal }}
          onKeyDown={(event) => { if (event.key === "Escape") { event.stopPropagation(); onCloseSearch?.(); } }}
          onMouseDown={(event) => { if (event.target === event.currentTarget) { event.preventDefault(); onCloseSearch?.(); } }}>
          <div ref={dialogRef} className="one-row-search-modal" role="dialog" aria-modal="true"
            aria-label="Search portfolio" data-testid="universal-search-modal">
      <UniversalSearchTrigger
        onOpenSearch={onOpenSearch}
        onCloseSearch={onCloseSearch}
        onClearSearch={onClearSearch}
        onQueryChange={onQueryChange}
        onKeyDown={onSearchKeyDown}
        inputRef={searchInputRef}
        open={searchOpen}
        query={searchQuery}
        modal
      />
      {searchPalette}
          </div>
        </div>, document.body)}
    </div>
  );
}

export function TopBarControls({
  currentUser,
  currentRole,
  flags,
  onRoleChange,
  onOpenSearch,
  onCloseSearch,
  onClearSearch,
  onQueryChange,
  onSearchKeyDown,
  searchInputRef,
  searchOpen,
  searchQuery,
  searchPalette = null,
  showSearch = true,
  showSeparator = false,
}) {
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const skipTriggerClickRef = useRef(false);

  const displayName = currentUser?.display_name === "Dev Workplace View"
    ? "Dev Viewer"
    : currentUser?.display_name === "Dev Full View"
      ? "Dev Viewer"
      : currentUser?.display_name ?? "Admin";
  const email       = currentUser?.email ?? "";
  const initials    = getInitials(displayName);
  const { photoSrc, onPhotoError } = useAccountPhoto(
    currentUser?.id ?? currentUser?.okta_sub ?? email, resolveAccountPhotoUrl(currentUser),
  );
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  function openPhotoDialog() {
    setUserMenuOpen(false);
    setPhotoDialogOpen(true);
  }
  const rolePill    = getRolePill(currentRole);
  const canControlDemoRole = currentUser?.role === "admin" && !currentUser?.devActAs;

  function handleLogout() {
    window.location.href = "/api/logout";
  }

  return (
    <>
      <div data-testid="v2-topbar-controls" style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        {showSearch && (
          <div style={{ position: "relative" }}>
            <UniversalSearchTrigger
              onOpenSearch={onOpenSearch}
              onCloseSearch={onCloseSearch}
              onClearSearch={onClearSearch}
              onQueryChange={onQueryChange}
              onKeyDown={onSearchKeyDown}
              inputRef={searchInputRef}
              open={searchOpen}
              query={searchQuery}
            />
            {searchPalette}
          </div>
        )}

        {showSeparator && (
          <div style={{ width: 1, alignSelf: "stretch", background: "var(--v2-line)", margin: "4px 2px" }} />
        )}

        <div style={{ position: "relative" }}>
          <button
            ref={userMenuRef}
            data-testid="v2-user-btn"
            onMouseDown={() => {
              skipTriggerClickRef.current = userMenuOpen;
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (skipTriggerClickRef.current) {
                skipTriggerClickRef.current = false;
                return;
              }
              setUserMenuOpen((o) => !o);
            }}
            style={{
              display: "flex", alignItems: "center", gap: 9,
              padding: "5px 8px 5px 6px", borderRadius: 9,
              cursor: "pointer", border: "none", background: "transparent",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--v2-hair)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            {photoSrc ? (
              <img
                src={photoSrc}
                onError={onPhotoError}
                referrerPolicy="no-referrer"
                alt={displayName}
                style={{
                  width: 32, height: 32, borderRadius: "50%",
                  objectFit: "cover", flexShrink: 0,
                }}
              />
            ) : (
              <span style={{
                width: 32, height: 32, borderRadius: "50%",
                background: "linear-gradient(135deg, var(--v2-user-grad-a), var(--v2-user-grad-b))",
                flexShrink: 0, display: "inline-flex",
                alignItems: "center", justifyContent: "center",
                fontSize: T.fsXxs5, fontWeight: T.fwBold, color: "var(--v2-card)",
                userSelect: "none",
              }}>
                {initials}
              </span>
            )}
            <span className="one-row-account-name" style={{ fontSize: T.fsSm5, fontWeight: T.fwBold, color: "var(--v2-dark)", whiteSpace: "nowrap" }}>
              {displayName}
            </span>
            <ChevronDown size={14} style={{ color: "var(--v2-light)" }} />
          </button>

          {userMenuOpen && (
            <Popover
              data-testid="v2-user-menu"
              role="menu"
              variant="menu"
              portalToBody
              horizontalViewportInset={16}
              triggerRef={userMenuRef}
              onClose={() => setUserMenuOpen(false)}
              style={{
                minWidth: 268,
                "--dev-act-as-max-width": "100%",
              }}
            >
              <div
                data-testid="v2-account-identity"
                style={{
                  display: "flex", alignItems: "center", gap: 11,
                  padding: "14px 14px 12px",
                }}
              >
                <ChangePhotoAvatar onActivate={openPhotoDialog}>
                {photoSrc ? (
                  <img
                    src={photoSrc}
                    onError={onPhotoError}
                    referrerPolicy="no-referrer"
                    alt={displayName}
                    style={{
                      width: 38, height: 38, borderRadius: "50%",
                      objectFit: "cover", flexShrink: 0,
                    }}
                  />
                ) : (
                  <span
                    aria-hidden="true"
                    style={{
                      width: 38, height: 38, borderRadius: "50%",
                      background: "var(--v2-dark)", color: T.white,
                      fontSize: T.fsBase, fontWeight: T.fwBold,
                      display: "inline-flex", alignItems: "center",
                      justifyContent: "center", flexShrink: 0,
                      userSelect: "none",
                    }}
                  >
                    {initials}
                  </span>
                )}
                </ChangePhotoAvatar>
                <div style={{ minWidth: 0 }}>
                  <div
                    data-testid="v2-account-name"
                    style={{
                      fontSize: T.fsBase, fontWeight: T.fwBold,
                      color: T.dark, lineHeight: 1.2,
                      overflow: "hidden", textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {displayName}
                  </div>
                  {email && (
                    <div
                      data-testid="v2-account-email"
                      style={{
                        fontSize: T.fsXs, color: T.mid, marginTop: 2,
                        overflow: "hidden", textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {email}
                    </div>
                  )}
                  <span
                    data-testid="v2-account-role"
                    style={{
                      display: "inline-block", marginTop: 5,
                      borderRadius: 5, padding: "2px 7px",
                      fontSize: T.fsXxxs, fontWeight: T.fwBold,
                      letterSpacing: "0.02em", textTransform: "uppercase",
                      background: rolePill.bg, color: rolePill.fg,
                    }}
                  >
                    {rolePill.label}
                  </span>
                </div>
              </div>

              <button
                type="button"
                role="menuitem"
                data-testid="v2-change-photo"
                style={{ ...accountMenuItemStyle, color: "var(--v2-accent)" }}
                onClick={openPhotoDialog}
              >
                <Camera size={15} style={accountMenuIconStyle} />
                <span>Change photo</span>
              </button>

              <div style={menuDividerStyle} />

              {canControlDemoRole && (
                <>
                  {currentRole === "admin" && (
                    <>
                      <button
                        type="button"
                        role="menuitem"
                        data-testid="v2-admin-settings"
                        style={accountMenuItemStyle}
                        onClick={() => {
                          setUserMenuOpen(false);
                          navigate("/admin");
                        }}
                      >
                        <Settings size={15} style={accountMenuIconStyle} />
                        <span>Admin settings</span>
                      </button>

                      <div style={menuDividerStyle} />
                    </>
                  )}

                </>
              )}

              {/* DEMO ONLY - gated by the server, independent of target role. */}
              <DevActAsControl currentRole={currentRole} onRoleChange={onRoleChange} />

              <button
                type="button"
                role="menuitem"
                data-testid="v2-sign-out"
                style={accountMenuItemStyle}
                onClick={handleLogout}
              >
                <LogOut size={15} style={accountMenuIconStyle} />
                <span>Sign out</span>
              </button>
            </Popover>
          )}
          {photoDialogOpen && (
            <ProfilePhotoDialog
              currentUser={currentUser}
              displayName={displayName}
              returnFocusRef={userMenuRef}
              onClose={() => setPhotoDialogOpen(false)}
            />
          )}
        </div>
      </div>

    </>
  );
}

const ROLE_PILL = {
  admin:        { label: "Admin",        bg: T.blueSoft, fg: T.blue },
  editor:       { label: "Editor",       bg: T.blueSoft, fg: T.blue },
  viewer:       { label: "Viewer", bg: T.lightGray, fg: T.mid },
  view_limited: { label: "Viewer", bg: T.lightGray, fg: T.mid },
};
function getRolePill(role) {
  return ROLE_PILL[role] ?? { label: getRoleDisplayLabel(role), bg: T.lightGray, fg: T.mid };
}

// Two-buildings stepped glyph — matches the design brief for Offices/portfolio.
// lucide-react's Building2 (v0.511.0) is a centre-tower icon, not this shape.
function OfficesIcon({ size = 24, strokeWidth = 2, ...props }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M4 20V6l7-3v17M11 20h9V9l-9-3" />
    </svg>
  );
}

const NAV_ICON_MAP = {
  home:      Home,
  // NAV-RESTRUCTURE-RADAR: Radar → InFocusRadarPage; Playbooks → stub page.
  radar:     Target,
  playbooks: BookOpen,
  portfolio: OfficesIcon,
  places: OfficesIcon,
  leases:    FileText,
  signals:   Activity,
  projects:  FolderKanban,
  plan:      Compass,
  reports:   BarChart3,
  roadmap:   Map,
  workflows: ListChecks,
  okrs:      ListChecks,
};

const EASE = [0.4, 0, 0.2, 1];

// ─── GLOBAL-TITLE-HEADER-V3 style constants ────────────────────────────────
//
// ONE authoritative source for all topbar metrics. Future per-page edits
// MUST NOT duplicate or override these values inline — that is what kept
// regressing the title baseline before V3.
//
//   object-title-row  — Home-measured title height (shared with record chrome)
//   TOPBAR_PADDING     — "top right bottom" shorthand; left/right padding for
//                        the row; the row itself is vertically centered so
//                        the title and TopBarControls (Ask AI/bell/avatar)
//                        line up on the same baseline
  //   SUBNAV_BACKGROUND  — default for non-collection back-link bands
//   SUBNAV_BORDER      — divider between subnav band and the controls row
//
// Before V3: the isTopLevel branch in topbar-left added a back-link that
// pushed the title down on sub-pages, and topBarActions injected pager
// buttons into the white bar right — both are now in the sub-nav band.
const TOPBAR_PADDING     = "0 22px";
const SUBNAV_BACKGROUND  = "var(--v2-card)";
const SUBNAV_BORDER      = "1px solid var(--v2-line)";

export function ShellV2({
  children,
  currentUser,
  currentRole,
  actualRole,
  onNavigate,
  onRoleChange,
  onExitRolePreview,
  onOpenSearch,
  onCloseSearch,
  onClearSearch,
  onQueryChange,
  onSearchKeyDown,
  searchInputRef,
  searchOpen = false,
  searchQuery = "",
  searchPalette = null,
  activeView = "home",
  pageLabel = "Home",
  // Descriptive pageSubtitle is intentionally no longer rendered. Functional
  // collection status must opt in separately rather than being lost with copy.
  pageStatus = null,
  // GLOBAL-TITLE-HEADER-V3: new render-prop slots for the sub-nav band.
  // subNavLeft  — back-link breadcrumb; rendered on the LEFT of the tinted band.
  // subNavRight — page-level nav (e.g. prev/next pager); rendered on the RIGHT.
  // Band only appears when at least one of these is provided, so top-level
  // pages (Pulse, Locations) get no band at all.
  subNavLeft  = null,
  subNavRight = null,
  // subNavStyle — optional style overrides for the sub-nav band background and
  // border. Defaults to SUBNAV_BACKGROUND / SUBNAV_BORDER so callers that don't
  // supply this prop see no change. InFocusRadarPage passes white-card values to
  // match the Offices detail 2nd-row nav pattern.
  subNavStyle = null,
  // Collection navigation shares the title appearance, not back-link styling.
  collectionSubNav = false,
  oneRow = false,
  sectionLabel = null,
  headerLeft = null,
  headerActions = null,
  // LOCATION-HEADER-COLLAPSE-S1: when true, the white top bar is suppressed
  // entirely so the page's own header band (e.g. LocationDetail unified bar)
  // becomes the sole top-of-page chrome. Only used for location-detail in v2.
  hideTopBar = false,
  // Full project detail must remain readable with the initial mobile rail.
  // Other routes retain their existing navigation behavior.
  autoCollapseNavigation = false,
  // Deprecated props — kept for backward compat. When subNavLeft/subNavRight
  // are absent, these are used to derive them automatically so existing
  // callers continue to work without changes.
  isTopLevel    = true,   // deprecated: band presence is now derived from subNav slots
  contentOwnsScroll = true,
  onBack        = null,   // deprecated: pass subNavLeft instead
  backLabel     = "Back", // deprecated: pass subNavLeft instead
  topBarActions = null,   // deprecated: pass subNavRight instead
  // Keyboard prev/next — unchanged.
  onKeyboardPrev = null,
  onKeyboardNext = null,
}) {
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    if (!autoCollapseNavigation || typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia("(max-width: 860px)");
    const collapseOnNarrow = () => { if (media.matches) setCollapsed(true); };
    collapseOnNarrow();
    media.addEventListener("change", collapseOnNarrow);
    return () => media.removeEventListener("change", collapseOnNarrow);
  }, [autoCollapseNavigation]);
  const navigate = useNavigate();
  const sensitivity = useSensitivityPolicy();
  const { summary: reconciliationSummary } = useDataReconciliationSummary(currentRole);
  const authenticatedRole = actualRole ?? (
    currentUser?.role === "view_limited" ? "viewer" : currentUser?.role
  );

  const { flags, flagsReady, nav_order, nav_config, nav_labels } = useFeatureFlagContext();
  const { items: navItems, disabledKeys } = flagsReady
    ? getRailNavItems(
        flags,
        nav_order,
        nav_labels ?? {},
        {
          previewMode: flags.preview_mode,
          isAdmin: currentRole === "admin",
          navConfig: nav_config,
          canSee: sensitivity.canSee,
        },
      )
    : { items: [], disabledKeys: new Set() };

  // GLOBAL-TITLE-HEADER-V2 keyboard nav — ArrowLeft / ArrowRight.
  // Fires whenever the Shell has prev/next callbacks (subnav pager).
  // Runs at Shell level so it works even if the detail body hasn't mounted yet.
  // Guard: skip when a text-entry element has focus so typing is unaffected.
  useEffect(() => {
    if (!onKeyboardPrev && !onKeyboardNext) return;
    function onKeyDown(e) {
      const tag = (document.activeElement?.tagName ?? "").toUpperCase();
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
      if (e.key === "ArrowLeft"  && onKeyboardPrev) onKeyboardPrev();
      if (e.key === "ArrowRight" && onKeyboardNext) onKeyboardNext();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onKeyboardPrev, onKeyboardNext]);

  function handleNavClick(targetView) {
    if (onNavigate) onNavigate(targetView);
  }

  // ── GLOBAL-TITLE-HEADER-V3: backward-compat derivation ──────────────────
  //
  // When the new subNavLeft/subNavRight slots are absent, fall back to the
  // deprecated onBack/backLabel/topBarActions props so existing callers
  // (and existing tests) continue to work without modification.
  //
  // Backward-compat back-link keeps the "v2-topbar-back" testid so any test
  // that queries that id finds the element (now in the subnav band instead
  // of the white topbar — the DOM position change is intentional and correct).
  const subNavLeftResolved = subNavLeft ?? (
    (!isTopLevel && onBack)
      ? (
        <button
          data-testid="v2-topbar-back"
          onClick={onBack}
          style={{
            background: "none", border: "none", cursor: "pointer", padding: 0,
            display: "inline-flex", alignItems: "center", gap: 5,
            color: "var(--v2-accent)", fontSize: T.fsSm, fontWeight: T.fwSemibold,
          }}
        >
          ‹ {backLabel}
        </button>
      )
      : null
  );
  const subNavRightResolved = subNavRight ?? topBarActions ?? null;
  const hasSubNav = Boolean(subNavLeftResolved || subNavRightResolved);

  return (
    <div className="v2-app" data-testid="v2-shell">
      {/* ── Left nav ── */}
      <motion.nav
        data-testid="v2-nav"
        animate={{ width: collapsed ? 60 : 260 }}
        transition={{ duration: 0.22, ease: EASE }}
        style={{
          background: "var(--v2-nav-bg)",
          borderRight: "1px solid var(--v2-nav-border)",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          overflow: "hidden",
          // NAV-Z-TRAP-FIX: As a flex child of .v2-app, setting zIndex creates a
          // stacking context that ensures the nav always renders above any
          // position:fixed content-area overlays (e.g. SlideSheet scrim at 1100).
          zIndex: LAYERS.stickyChrome,
        }}
      >
        {/* Nav top */}
        <div
          style={{
            display: "flex",
            flexDirection: collapsed ? "column" : "row",
            alignItems: "center",
            gap: collapsed ? 7 : 10,
            padding: collapsed ? "14px 0 10px" : "14px 12px 10px",
            justifyContent: collapsed ? "center" : "flex-start",
          }}
        >
          {/* Logo mark + wordmark — clickable, navigates back to Pulse (home) */}
          <button
            type="button"
            data-testid="v2-logo-home-link"
            onClick={() => handleNavClick("home")}
            aria-label="Go to Home"
            style={{
              display: "flex",
              alignItems: "center",
              gap: collapsed ? 0 : 10,
              border: "none",
              background: "transparent",
              padding: 0,
              cursor: "pointer",
              flexShrink: 0,
              textAlign: "left",
            }}
          >
            <div
              data-testid="v2-logo"
              style={{
                width: 28, height: 28, borderRadius: 8,
                background: "var(--v2-card)",
                boxShadow: "0 1px 4px rgba(0,0,0,.18)",
                flexShrink: 0, display: "grid", placeItems: "center",
              }}
            >
              <svg viewBox="0 0 32 32" width="18" height="18" fill="var(--v2-tile-blue-fg)">
                <path d="M27.401 19.531c-1.131-0.645-2.407-0.837-3.672-0.885-1.052-0.031-2.631-0.724-2.631-2.645 0-1.432 1.156-2.588 2.647-2.645 1.265-0.048 2.541-0.24 3.671-0.891 3.193-1.844 4.292-5.928 2.448-9.125-1.859-3.199-5.952-4.287-9.156-2.437-2.072 1.187-3.348 3.401-3.339 5.787 0 1.296 0.464 2.484 1.052 3.599 0.496 0.927 0.735 2.661-0.948 3.635-1.265 0.724-2.843 0.272-3.624-0.989-0.661-1.068-1.459-2.063-2.589-2.708-3.197-1.849-7.291-0.751-9.124 2.437-1.839 3.199-0.745 7.281 2.452 9.125 2.068 1.187 4.609 1.187 6.677 0 1.125-0.647 1.923-1.641 2.584-2.708 0.541-0.871 1.911-1.985 3.624-0.991 1.267 0.719 1.657 2.319 0.948 3.641-0.583 1.093-1.052 2.297-1.052 3.593 0 3.688 2.991 6.672 6.677 6.677 3.688 0 6.672-2.989 6.677-6.677 0.011-2.385-1.255-4.599-3.323-5.792z"/>
              </svg>
            </div>

            {/* Wordmark (hidden when collapsed) */}
            <motion.div
              animate={{ opacity: collapsed ? 0 : 1, width: collapsed ? 0 : "auto" }}
              transition={{ duration: 0.16, ease: EASE }}
              style={{ overflow: "hidden", flexShrink: 0, height: collapsed ? 0 : undefined }}
            >
              <div style={{ fontSize: T.fsLg, fontWeight: T.fwBold, lineHeight: 1.05, whiteSpace: "nowrap", color: "var(--v2-card)" }}>{APP_NAME}</div>
            </motion.div>
          </button>

          {/* Collapse toggle */}
          <button
            data-testid="v2-nav-toggle"
            onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? "Expand" : "Collapse"}
            style={{
              marginLeft: collapsed ? 0 : "auto",
              color: "var(--v2-nav-text)", cursor: "pointer",
              width: 26, height: 26, borderRadius: 7,
              display: "grid", placeItems: "center",
              border: "none", background: "transparent",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--v2-nav-hover)"; e.currentTarget.style.color = "var(--v2-card)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--v2-nav-text)"; }}
          >
            <motion.span
              animate={{ rotate: collapsed ? 180 : 0 }}
              transition={{ duration: 0.22, ease: EASE }}
              style={{ display: "inline-flex" }}
            >
              <ChevronsLeft size={16} />
            </motion.span>
          </button>
        </div>

        {/* Nav scroll area */}
        <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: collapsed ? "8px 0" : "8px 12px" }}>

          {navItems.map((item) => (
              <React.Fragment key={item.key}>
                {disabledKeys.has(item.key) ? (
                  <div
                    data-testid={`v2-nav-item-disabled-${item.key}`}
                    title={item.label}
                    aria-disabled="true"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: collapsed ? "center" : "flex-start",
                      gap: 11,
                      padding: collapsed ? "9px 0" : "9px 10px",
                      borderRadius: 8,
                      cursor: "default",
                      marginBottom: 2,
                      whiteSpace: "nowrap",
                      color: "var(--v2-nav-text)",
                      fontSize: T.fsBase,
                      fontWeight: T.fwSemibold,
                      opacity: 0.35,
                      pointerEvents: "none",
                    }}
                  >
                    <span style={{ flexShrink: 0, display: "grid", placeItems: "center" }}>
                      {(() => { const Icon = NAV_ICON_MAP[item.key] ?? Activity; return <Icon size={16} />; })()}
                    </span>
                    {!collapsed && <span>{item.label}</span>}
                  </div>
                ) : (
                  <NavItem
                    navKey={item.key}
                    label={item.label}
                    Icon={NAV_ICON_MAP[item.key] ?? Activity}
                    badge={null}
                    isActive={item.views.includes(activeView)}
                    collapsed={collapsed}
                    onClick={() => handleNavClick(item.targetView)}
                  />
                )}
              </React.Fragment>
          ))}

        </div>

        {/* Footer nav — tour is available to every role; Admin remains gated */}
        <div style={{ borderTop: "1px solid var(--v2-nav-border)", padding: collapsed ? "8px 0 12px" : "8px 12px 12px" }}>
          <NavItem
            navKey="tour"
            label="Take a tour"
            Icon={Compass}
            badge={null}
            isActive={activeView === "start-here"}
            collapsed={collapsed}
            onClick={() => navigate("/start-here")}
          />
          {currentRole === "admin" && (
            <NavItem
              navKey="admin"
              label="Admin"
              Icon={Settings}
              badge={reconciliationSummary?.activeCount > 0
                ? { count: reconciliationSummary.activeCount, tone: "red" }
                : null}
              isActive={activeView === "admin"}
              collapsed={collapsed}
              onClick={() => handleNavClick("admin")}
            />
          )}
        </div>
      </motion.nav>

      {/* ── Right side: topbar + subnav band + content ── */}
      <div className="v2-main">

        {/* ── WHITE TOP BAR — global chrome only ──────────────────────────── */}
        {/*   Left:  page title h1 at a FIXED top offset (TOPBAR_PADDING top)  */}
        {/*          so the title baseline is identical on every page.           */}
        {/*   Right: Ask AI · user menu — nothing else. */}
        {/*   The back-link and prev/next pager are in the SUB-NAV BAND below.  */}
        {/*   LOCATION-HEADER-COLLAPSE-S1: hidden on detail-header routes. */}
        {!hideTopBar && <div
          data-testid="v2-topbar"
          className={`v2-page-topbar v2-chrome-strip object-title-row object-title-single${activeView === "home" ? " is-home-header" : ""}${hasSubNav && !oneRow ? " object-title-with-nav" : ""}${oneRow ? " one-row-header" : ""}${searchOpen ? " is-search-open" : ""}`}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: TOPBAR_PADDING,
            flexShrink: 0,
            position: "relative",
            zIndex: searchOpen ? LAYERS.modal : LAYERS.stickyChrome,
          }}
        >
          {/* Left: title block — always at the same vertical position */}
          <div
            data-testid="v2-topbar-left"
            className="v2-header-identity"
            style={{ display: "flex", flexDirection: oneRow ? "row" : "column", alignItems: oneRow ? "center" : undefined, justifyContent: "flex-start", gap: oneRow ? 14 : 1, minWidth: 0 }}
          >
            {activeView === "home" ? <HomeHeader currentUser={currentUser} currentRole={currentRole} actualRole={actualRole} /> : <>
            {collapsed && oneRow && <span className="one-row-section-label" data-testid="collapsed-section-label">{sectionLabel}</span>}
            {oneRow && (headerLeft ?? subNavLeftResolved)}
            {pageLabel && (!oneRow || headerLeft) && (
              isTopLevel ? (
                <div data-testid="v2-page-header">
                  <h1 className="v2-header-line" data-testid="page-label" style={{ fontSize: T.fsLg5, fontWeight: T.fwBold, margin: 0, color: "var(--v2-dark)", lineHeight: 1.15 }}>
                    {pageLabel}
                  </h1>
                  {pageStatus && (
                    <div className="v2-header-line" data-testid="v2-page-status" style={{ fontSize: T.fsSm, color: "var(--v2-light)", marginTop: 2 }}>
                      {pageStatus}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <h1 className="v2-header-line" data-testid="page-label" style={{ fontSize: T.fsLg5, fontWeight: T.fwBold, margin: 0, color: "var(--v2-dark)", lineHeight: 1.15 }}>
                    {pageLabel}
                  </h1>
                  {pageStatus && (
                    <div className="v2-header-line" data-testid="v2-page-status" style={{ fontSize: T.fsSm, color: "var(--v2-light)", marginTop: 2 }}>
                      {pageStatus}
                    </div>
                  )}
                </>
              )
            )}
            </>}
          </div>

          {/* Center: one stable search anchor, independent of the page title. */}
          <div className="one-row-header-controls">
          {headerActions}
          <CenteredSearchAnchor
            onOpenSearch={onOpenSearch}
            onCloseSearch={onCloseSearch}
            onClearSearch={onClearSearch}
            onQueryChange={onQueryChange}
            onSearchKeyDown={onSearchKeyDown}
            searchInputRef={searchInputRef}
            searchOpen={searchOpen}
            searchQuery={searchQuery}
            searchPalette={searchPalette}
          />

          {/* Right: global chrome — bell · user (shared TopBarControls) */}
          <TopBarControls
            currentUser={currentUser}
            currentRole={currentRole}
            flags={flags}
            onRoleChange={onRoleChange}
            onOpenSearch={onOpenSearch}
            showSearch={false}
          />
          </div>
        </div>}

        {currentRole && authenticatedRole && currentRole !== authenticatedRole && (
          <div
            className="v2-role-preview-band"
            data-testid="role-preview-band"
            role="status"
          >
            <span>
              Previewing as <strong>{getRoleDisplayLabel(currentRole)}</strong>
            </span>
            <button
              type="button"
              data-testid="exit-role-preview"
              onClick={onExitRolePreview}
            >
              Exit preview
            </button>
          </div>
        )}

        {/* ── SUB-NAV BAND — continuous white header, sub-pages only ────────── */}
        {/*   Present only when subNavLeft or subNavRight is provided.           */}
        {/*   Left:  back-link breadcrumb (e.g. "‹ Pulse", "‹ Locations").      */}
        {/*   Right: page-level nav (e.g. location prev/next pager).            */}
        {/*   Top-level pages (Pulse, Locations) produce no band.               */}
        {hasSubNav && !oneRow && (
          <div
            data-testid="v2-subnav-band"
            className="object-subnav-row"
            style={{
              background: collectionSubNav ? undefined : (subNavStyle?.background ?? SUBNAV_BACKGROUND),
              borderBottom: collectionSubNav ? undefined : (subNavStyle?.borderBottom ?? SUBNAV_BORDER),
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 22px",
              flexShrink: 0,
            }}
          >
            <div style={{ minWidth: 0, maxWidth: "100%" }}>{subNavLeftResolved}</div>
            <div style={{ flexShrink: 0 }}>{subNavRightResolved}</div>
          </div>
        )}

        {/* Page content */}
        <UniversalSearchOpenContext.Provider value={searchOpen}>
          <SidebarChromeContext.Provider value={collapsed}>
          <div className={`v2-content${contentOwnsScroll ? "" : " is-contained"}`}>
            {children}
          </div>
          </SidebarChromeContext.Provider>
        </UniversalSearchOpenContext.Provider>
      </div>
    </div>
  );
}


export function NavItem({ navKey, label, Icon, badge, isActive, collapsed, onClick }) {
  const [hovered, setHovered] = useState(false);
  const intent = useHoverIntent({ enabled: collapsed });
  const isTour = navKey === "tour";

  const color     = isTour ? T.tourCtaInk : isActive ? "var(--v2-card)" : hovered ? "var(--v2-card)" : "var(--v2-nav-text)";
  const iconColor = isTour ? T.tourCtaInk : isActive ? "var(--v2-card)" : hovered ? "var(--v2-card)" : "var(--v2-nav-text)";
  const fw        = isActive ? T.fwBold : T.fwSemibold;

  return (
    <div
      data-testid={`v2-nav-item-${navKey}`}
      {...intent.triggerProps}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { intent.triggerProps.onKeyDown(e); if (e.key === "Enter") onClick(); }}
      onMouseEnter={() => { setHovered(true); intent.enter(); }}
      onMouseLeave={(event) => { setHovered(false); intent.triggerProps.onMouseLeave(event); }}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: collapsed ? "center" : "flex-start",
        gap: 11,
        padding: collapsed ? "9px 0" : "9px 10px",
        borderRadius: 8,
        cursor: "pointer",
        marginBottom: 2,
        whiteSpace: "nowrap",
        color,
        fontSize: T.fsBase,
        fontWeight: fw,
        /* Active background is supplied by the layoutId motion.div below */
        background: isTour ? T.tourCtaBg : isActive ? "transparent" : hovered ? "var(--v2-nav-hover)" : "transparent",
        transition: "background .15s var(--v2-ease), color .15s var(--v2-ease)",
      }}
    >
      {/* Layout-animated active background — moves between nav items via shared layoutId */}
      {isActive && !isTour && (
        <motion.div
          layoutId="v2-nav-active"
          initial={false}
          style={{
            position: "absolute", inset: 0, borderRadius: 8,
            background: "var(--v2-nav-active-bg)",
            zIndex: 0,
          }}
          transition={{ duration: 0.2, ease: EASE }}
        />
      )}

      <span style={{ flexShrink: 0, display: "grid", placeItems: "center", color: iconColor, position: "relative", zIndex: 1 }}>
        <Icon size={16} />
      </span>

      {/* AnimatePresence guards the label so it fades on collapse/expand */}
      <AnimatePresence mode="wait">
        {!collapsed && (
          <motion.span
            key={`v2-nav-label-${navKey}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12, ease: EASE }}
            style={{ flex: 1, position: "relative", zIndex: 1 }}
          >
            {label}
          </motion.span>
        )}
      </AnimatePresence>

      {badge && !collapsed && (
        <span style={{
          fontSize: T.fsXxs5, fontWeight: T.fwBold, padding: "1px 7px",
          borderRadius: 999,
          background: badge.tone === "red" ? "var(--v2-red-tint)" : "var(--v2-amber-tint)",
          color: badge.tone === "red" ? "var(--v2-red)" : "var(--v2-amber)",
          marginLeft: "auto",
          position: "relative", zIndex: 1,
        }} data-testid={navKey === "admin" ? "v2-nav-reconciliation-count" : undefined}>
          {badge.count}
        </span>
      )}

      {badge && collapsed && (
        <span
          data-testid={navKey === "admin" ? "v2-nav-reconciliation-count" : undefined}
          style={{
            position: "absolute",
            top: 4,
            right: navKey === "admin" ? 6 : 13,
            minWidth: navKey === "admin" ? 14 : 7,
            height: navKey === "admin" ? 14 : 7,
            padding: navKey === "admin" ? "0 3px" : 0,
            borderRadius: 8,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: T.fsXxxs,
            fontWeight: T.fwBold,
            lineHeight: 1,
            color: navKey === "admin" ? "var(--v2-card)" : "transparent",
            background: badge.tone === "red" ? "var(--v2-red)" : "var(--v2-amber)",
            border: "1.5px solid var(--v2-nav-bg)",
            zIndex: 2,
            boxSizing: "border-box",
          }}
        >
          {navKey === "admin" ? badge.count : null}
        </span>
      )}

      {/* Tooltip (collapsed only) */}
      {intent.open && (
        <span role="tooltip" style={{
          position: "absolute", left: 56,
          background: "var(--v2-dark)", color: "var(--v2-card)",
          fontSize: T.fsSm, fontWeight: T.fwSemibold, padding: "5px 9px",
          borderRadius: 7, whiteSpace: "nowrap", zIndex: 10,
          pointerEvents: "none",
        }}>
          {label}
        </span>
      )}
    </div>
  );
}

const menuDividerStyle = {
  height: 1,
  background: T.border,
  margin: 0,
};

const accountMenuIconStyle = {
  color: T.mid,
  flexShrink: 0,
};

const accountMenuItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  width: "100%",
  border: "none",
  background: "transparent",
  padding: "9px 14px",
  textAlign: "left",
  fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, sans-serif",
  fontSize: T.fsSm5,
  fontWeight: T.fwSemibold,
  color: T.dark,
  cursor: "pointer",
};
